import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { uiScope } from '@/lib/cssScope'
import styles from '@/styles/components/ui/Button.module.css'

export type ButtonColor = 'primary' | 'secondary' | 'danger' | 'outline' | 'brand' | 'accent'
export type ButtonSize = 'small' | 'medium' | 'large'

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  /** Color / appearance */
  color?: ButtonColor
  size?: ButtonSize
  /** Stretch to container width */
  fullWidth?: boolean
  /** Tight padding when content is icon-only */
  iconOnly?: boolean
  children: ReactNode
  /** Icon next to label (see `iconPosition`). */
  icon?: ReactNode
  /** Place the icon before or after the label. */
  iconPosition?: 'start' | 'end'
  /** Appended to the root class (e.g. layout overrides). */
  className?: string
}

const colorClass: Record<ButtonColor, string> = {
  primary: styles['color--primary'],
  secondary: styles['color--secondary'],
  danger: styles['color--danger'],
  outline: styles['color--outline'],
  brand: styles['color--brand'],
  accent: styles['color--accent'],
}

const sizeClass: Record<ButtonSize, string> = {
  small: styles['size--small'],
  medium: styles['size--medium'],
  large: styles['size--large'],
}

export function Button({
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  iconOnly = false,
  type = 'button',
  icon,
  iconPosition = 'start',
  children,
  className = '',
  ...props
}: ButtonProps) {
  const root = [
    uiScope('button'),
    styles.button,
    colorClass[color],
    sizeClass[size],
    fullWidth ? styles['width--full'] : '',
    iconOnly ? styles['modifier--icon-only'] : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const iconEl = icon != null ? <span className={styles.button__icon}>{icon}</span> : null

  return (
    <button type={type} className={root} {...props}>
      {iconPosition === 'end' ? (
        <>
          {children}
          {iconEl}
        </>
      ) : (
        <>
          {iconEl}
          {children}
        </>
      )}
    </button>
  )
}

export default Button
